const chapter = {
  id: 'cell-unit-of-life',
  slug: 'cell-unit-of-life',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Cell — The Unit of Life',
        hindi: 'कोशिका — जीवन की इकाई',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Cell Biology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'cell-theory',
      title: {
        english: 'Cell Theory & Discovery',
        hindi: 'कोशिका सिद्धान्त और खोज',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Robert Hooke (1665)** — coined "cell" while observing cork (dead cells).',
        hindi: '**रॉबर्ट हुक (1665)** - कॉर्क (मृत कोशिकाओं) का अवलोकन करते हुए "सेल" गढ़ा गया।',
            },
            {
              english: '**Anton von Leeuwenhoek (1674)** — first observed living cell (bacteria, protozoa).',
        hindi: '**एंटोन वॉन लीउवेनहॉक (1674)** - पहली बार जीवित कोशिका (बैक्टीरिया, प्रोटोजोआ) देखी गई।',
            },
            {
              english: '**Robert Brown (1831)** — discovered nucleus.',
        hindi: '**रॉबर्ट ब्राउन (1831)** - केन्द्रक की खोज की।',
            },
            {
              english: '**Schleiden (1838) — botanist** & **Schwann (1839) — zoologist** → **Cell Theory**:',
        hindi: '**स्लेडेन (1838) - वनस्पतिशास्त्री** और **श्वान (1839) - प्राणीविज्ञानी** → **कोशिका सिद्धांत**:',
            },
            {
              english: '• All plants and animals are composed of cells.',
        hindi: '• सभी पौधे और जानवर कोशिकाओं से बने होते हैं।',
            },
            {
              english: '• Cell is the basic unit of life.',
        hindi: '• कोशिका जीवन की मूल इकाई है।',
            },
            {
              english: '**Rudolf Virchow (1855)** — *Omnis cellula e cellula* (all cells arise from pre-existing cells).',
        hindi: '**रुडोल्फ विरचो (1855)** — *ओम्निस सेल्युला ई सेल्युला* (सभी कोशिकाएँ पहले से मौजूद कोशिकाओं से उत्पन्न होती हैं)।',
            },
            {
              english: 'Modern Cell Theory: (i) all organisms are composed of cells; (ii) cell is structural & functional unit; (iii) cells arise from pre-existing cells.',
        hindi: 'आधुनिक कोशिका सिद्धांत: (i) सभी जीव कोशिकाओं से बने हैं; (ii) कोशिका संरचनात्मक एवं कार्यात्मक इकाई है; (iii) कोशिकाएँ पहले से मौजूद कोशिकाओं से उत्पन्न होती हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'pro-vs-eu',
      title: {
        english: 'Prokaryotic vs Eukaryotic Cells',
        hindi: 'प्रोकैरियोटिक बनाम यूकैरियोटिक कोशिकाएँ',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'Prokaryotic', 'Eukaryotic'],
          rows: [
            ['Size', '1–10 μm', '5–100 μm'],
            ['Nucleus', 'Nucleoid (no membrane)', 'True nucleus with envelope'],
            ['DNA', 'Single circular + plasmids', 'Linear, multiple chromosomes with histones'],
            ['Membrane organelles', 'Absent', 'Present (ER, Golgi, mito, etc.)'],
            ['Ribosomes', '70S (50S + 30S)', '80S cytoplasmic (60S + 40S); 70S in mito/chloro'],
            ['Cell wall', 'Peptidoglycan (bacteria)', 'Cellulose (plants), chitin (fungi), absent (animals)'],
            ['Cell division', 'Binary fission', 'Mitosis / Meiosis'],
            ['Examples', 'Bacteria, archaea, mycoplasma', 'Protists, fungi, plants, animals'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Prokaryotic special structures: **mesosomes** (infoldings of plasma membrane — respiration, secretion); **chromatophores** (photosynthetic membranes in cyanobacteria); **pili & fimbriae** (attachment); flagella with basal body, hook, filament.',
        hindi: 'प्रोकैरियोटिक विशेष संरचनाएँ: **मेसोसोम** (प्लाज्मा झिल्ली की तह - श्वसन, स्राव); **क्रोमैटोफोर्स** (साइनोबैक्टीरिया में प्रकाश संश्लेषक झिल्ली); **पिली और फ़िम्ब्रिए** (अटैचमेंट); बेसल बॉडी, हुक, फिलामेंट के साथ फ्लैगेल्ला।',
            },
            {
              english: '**Inclusion bodies** in cytoplasm — phosphate granules, cyanophycean granules, gas vacuoles. Not membrane-bound.',
        hindi: 'साइटोप्लाज्म में **समावेशन निकाय** - फॉस्फेट ग्रैन्यूल, साइनोफाइसीन ग्रैन्यूल, गैस रिक्तिकाएं। झिल्ली-बद्ध नहीं.',
            },
          ],
        },
      ],
    },
    {
      id: 'membrane',
      title: {
        english: 'Plasma Membrane & Cell Wall',
        hindi: 'प्लाज्मा झिल्ली और कोशिका भित्ति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Singer & Nicolson (1972)** — **Fluid Mosaic Model**. Phospholipid bilayer with proteins (integral & peripheral) embedded; lipids show **lateral movement (fluidity)**.',
        hindi: '**गायक और निकोलसन (1972)** - **द्रव मोज़ेक मॉडल**। प्रोटीन (अभिन्न और परिधीय) के साथ फॉस्फोलिपिड बाईलेयर एम्बेडेड; लिपिड **पार्श्व गति (तरलता)** दर्शाते हैं।',
            },
            {
              english: 'Phospholipids: hydrophilic head outward, hydrophobic tails inward.',
        hindi: 'फॉस्फोलिपिड्स: हाइड्रोफिलिक सिर बाहर की ओर, हाइड्रोफोबिक पूंछ अंदर की ओर।',
            },
            {
              english: '**Membrane transport**:',
        hindi: '**झिल्ली परिवहन**:',
            },
            {
              english: '• **Passive** — no ATP. Simple diffusion (down gradient), facilitated (carrier/channel), osmosis.',
        hindi: '• **निष्क्रिय** - कोई एटीपी नहीं। सरल प्रसार (नीचे की ओर ढाल), सुगम (carrier/channel), परासरण।',
            },
            {
              english: '• **Active** — uses ATP; against gradient. Na⁺/K⁺ pump (3 Na⁺ out, 2 K⁺ in per ATP).',
        hindi: '• **सक्रिय** — एटीपी का उपयोग करता है; ढाल के विरुद्ध. Na⁺/K⁺ पंप (3 Na⁺ बाहर, 2 K⁺ प्रति ATP)।',
            },
            {
              english: '**Plant cell wall** — outermost. Composition:',
        hindi: '**पौधे की कोशिका भित्ति** - सबसे बाहरी। संघटन:',
            },
            {
              english: '• Primary wall — cellulose, hemicellulose, pectin; plastic (capable of growth).',
        hindi: '• प्राथमिक दीवार - सेल्यूलोज, हेमिकेल्यूलोज, पेक्टिन; प्लास्टिक (विकास में सक्षम)।',
            },
            {
              english: '• Secondary wall — added later inside primary; lignified; rigid.',
        hindi: '• द्वितीयक दीवार - प्राथमिक के अंदर बाद में जोड़ी गई; लिग्निफाइड; कठोर।',
            },
            {
              english: '• Middle lamella — pectin; cements adjacent cells.',
        hindi: '• मध्य लामेला - पेक्टिन; आसन्न कोशिकाओं को मजबूत करता है।',
            },
            {
              english: 'Wall has **plasmodesmata** — cytoplasmic bridges connecting adjacent cells.',
        hindi: 'दीवार में **प्लाज्मोडेस्माटा** होता है - आसन्न कोशिकाओं को जोड़ने वाले साइटोप्लाज्मिक पुल। **एंडोप्लाज्मिक रेटिकुलम (ईआर)** - झिल्लीदार नलिकाओं का नेटवर्क।',
            },
          ],
        },
      ],
    },
    {
      id: 'organelles',
      title: {
        english: 'Cell Organelles',
        hindi: 'कोशिका अंगक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Endoplasmic reticulum (ER)** — network of membranous tubules.',
        hindi: '**एंडोप्लाज्मिक रेटिकुलम (ईआर)** - झिल्लीदार नलिकाओं का नेटवर्क।',
            },
            {
              english: '• **RER** — ribosomes attached; protein synthesis & secretion.',
        hindi: '• **आरईआर** — राइबोसोम जुड़े हुए; प्रोटीन संश्लेषण एवं स्राव.',
            },
            {
              english: '• **SER** — no ribosomes; lipid/steroid synthesis; detoxification (liver).',
        hindi: '• **SER** - कोई राइबोसोम नहीं; lipid/steroid संश्लेषण; विषहरण (यकृत)।',
            },
            {
              english: '**Golgi apparatus** (Camillo Golgi, 1898) — flattened cisternae stacked. **cis** face (forming, near ER) receives vesicles; **trans** face (maturing) ships to plasma membrane / lysosomes. Glycoprotein/glycolipid formation; cell secretion.',
        hindi: '**गोल्गी उपकरण** (कैमिलो गोल्गी, 1898) - चपटा सिस्टर्न स्टैक्ड। **सीआईएस** चेहरा (ईआर के पास बनता हुआ) पुटिकाओं को प्राप्त करता है; **ट्रांस** फेस (परिपक्व) जहाजों को प्लाज्मा झिल्ली / लाइसोसोम में भेजता है। Glycoprotein/glycolipid गठन; कोशिका स्राव.',
            },
            {
              english: '**Lysosomes** — single-membrane sacs with hydrolytic enzymes (acidic pH ≈ 5); **"suicidal bags"**; digest worn organelles, foreign material; contain proteases, lipases, nucleases.',
        hindi: '**लाइसोसोम** - हाइड्रोलाइटिक एंजाइमों (अम्लीय पीएच ≈ 5) के साथ एकल-झिल्ली थैली; **"आत्मघाती बैग"**; घिसे-पिटे अंगों, विदेशी पदार्थों को पचाना; इसमें प्रोटीज, लाइपेज, न्यूक्लीज होते हैं।',
            },
            {
              english: '**Vacuoles** — membrane-bound (tonoplast); large central vacuole in plants stores ions, sugars, pigments; contractile vacuole in protozoa for osmoregulation.',
        hindi: '**रिक्तिकाएँ** — झिल्ली-बद्ध (टोनोप्लास्ट); पौधों में बड़ी केंद्रीय रसधानियाँ आयनों, शर्कराओं, रंजकों को संग्रहित करती हैं; ऑस्मोरग्यूलेशन के लिए प्रोटोजोआ में संकुचनशील रिक्तिका।',
            },
            {
              english: '**Mitochondria** — double-membrane; outer smooth, inner folded into **cristae** with **F₁ particles (ATP synthase)**. Matrix has 70S ribosomes, circular DNA, enzymes of TCA cycle. **"Powerhouse of cell"** — site of aerobic respiration & ATP synthesis.',
        hindi: '**माइटोकॉन्ड्रिया** — दोहरी झिल्ली; बाहरी चिकना, आंतरिक **F₁ कणों (एटीपी सिंथेज़)** के साथ **क्रिस्टे** में मुड़ा हुआ। आव्यूह में 70S राइबोसोम, गोलाकार डीएनए, TCA चक्र के एंजाइम होते हैं। **"कोशिका का पावरहाउस"** - एरोबिक श्वसन और एटीपी संश्लेषण का स्थल।',
            },
            {
              english: '**Plastids** (only plants):',
        hindi: '**प्लास्टिड्स** (केवल पौधे):',
            },
            {
              english: '• **Chloroplasts** — chlorophyll; double-membrane + thylakoid stacks (grana) + stroma. Site of photosynthesis. 70S ribosomes, circular DNA.',
        hindi: '• **क्लोरोप्लास्ट** — क्लोरोफिल; डबल-झिल्ली + थायलाकोइड स्टैक्स (ग्रेना) + स्ट्रोमा। प्रकाश संश्लेषण का स्थल. 70S राइबोसोम, गोलाकार डीएनए।',
            },
            {
              english: '• Chromoplasts — fat-soluble carotenoid pigments (yellow/orange/red).',
        hindi: '• क्रोमोप्लास्ट - वसा में घुलनशील कैरोटीनॉयड वर्णक (yellow/orange/लाल)।',
            },
            {
              english: '• Leucoplasts — colourless; storage. Amyloplasts (starch), elaioplasts (oils), aleuroplasts (proteins).',
        hindi: '• ल्यूकोप्लास्ट - रंगहीन; भंडारण। एमाइलोप्लास्ट (स्टार्च), इलायोप्लास्ट (तेल), एल्यूरोप्लास्ट (प्रोटीन)।',
            },
            {
              english: '**Ribosomes** — non-membrane; rRNA + protein. Eukaryote cytoplasm 80S; prokaryote and mito/plastid 70S. Site of protein synthesis. **George Palade (1953)** — discovery.',
        hindi: '**राइबोसोम** - गैर-झिल्ली; आरआरएनए + प्रोटीन। यूकेरियोट साइटोप्लाज्म 80S; प्रोकैरियोट और mito/plastid 70S। प्रोटीन संश्लेषण का स्थल. **जॉर्ज पलाडे (1953)** — खोज।',
            },
            {
              english: '**Cytoskeleton** — microfilaments (actin), intermediate filaments, microtubules (tubulin). Shape, motility, intracellular transport.',
        hindi: '**साइटोस्केलेटन** - माइक्रोफिलामेंट्स (एक्टिन), मध्यवर्ती फिलामेंट्स, सूक्ष्मनलिकाएं (ट्यूबुलिन)। आकार, गतिशीलता, अंतःकोशिकीय परिवहन।',
            },
            {
              english: '**Cilia & Flagella** — hair-like; **9 + 2 axoneme** (9 outer doublets + 2 central singlets) covered by membrane; arise from basal body (centriole-derived). Cilia short & many; flagella longer & few.',
        hindi: '**सिलिया और फ्लैगेल्ला** - बाल जैसा; **9 + 2 एक्सोनोमी** (9 बाहरी दोहरे + 2 केंद्रीय एकल) झिल्ली से ढके हुए; बेसल बॉडी (सेंट्रीओल-व्युत्पन्न) से उत्पन्न होते हैं। सिलिया लघु एवं अनेक; कशाभिका लंबी और कम।',
            },
            {
              english: '**Centrosome & Centrioles** — animal cells; pair of centrioles at right angles; **9 + 0** triplet microtubule arrangement. Form spindle fibres in mitosis; basal bodies of cilia/flagella.',
        hindi: '**सेंट्रोसोम और सेंट्रीओल्स** - पशु कोशिकाएँ; समकोण पर सेंट्रीओल्स की जोड़ी; **9 + 0** त्रिक सूक्ष्मनलिका व्यवस्था। माइटोसिस में धुरी तंतुओं का निर्माण; cilia/flagella के बेसल निकाय।',
            },
            {
              english: '**Nucleus** — Robert Brown (1831). Double-membrane envelope with nuclear pores; contains **nucleoplasm**, **nucleolus** (rRNA synthesis), **chromatin** (DNA + histones — euchromatin loose & active; heterochromatin condensed inactive).',
        hindi: '**न्यूक्लियस** - रॉबर्ट ब्राउन (1831)। परमाणु छिद्रों के साथ डबल-झिल्ली लिफाफा; इसमें **न्यूक्लियोप्लाज्म**, **न्यूक्लियोलस** (आरआरएनए संश्लेषण), **क्रोमैटिन** (डीएनए + हिस्टोन - यूक्रोमैटिन ढीला और सक्रिय; हेटरोक्रोमैटिन संघनित निष्क्रिय) शामिल हैं।',
            },
            {
              english: '• **Chromosome morphology**: telocentric, acrocentric, sub-metacentric, metacentric (based on centromere position). Each chromosome has 2 chromatids joined at centromere; satellite at secondary constriction.',
        hindi: '• **गुणसूत्र आकृति विज्ञान**: टेलोसेंट्रिक, एक्रोसेंट्रिक, उप-मेटासेंट्रिक, मेटासेंट्रिक (सेंट्रोमियर स्थिति के आधार पर)। प्रत्येक गुणसूत्र में सेंट्रोमियर पर 2 क्रोमैटिड जुड़े होते हैं; द्वितीयक संकुचन पर उपग्रह.',
            },
          ],
        },
      ],
    },
    {
      id: 'plant-vs-animal',
      title: {
        english: 'Plant Cell vs Animal Cell',
        hindi: 'पादप कोशिका बनाम जन्तु कोशिका',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'Plant Cell', 'Animal Cell'],
          rows: [
            ['Cell wall', 'Present (cellulose)', 'Absent'],
            ['Plastids', 'Present', 'Absent'],
            ['Vacuole', 'Large central', 'Small or absent'],
            ['Centrosome', 'Absent (lower plants have)', 'Present'],
            ['Lysosomes', 'Rare', 'Present'],
            ['Storage', 'Starch', 'Glycogen'],
            ['Shape', 'Fixed rectangular', 'Variable, rounded'],
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Schleiden + Schwann (1838-39): Cell theory}',
      desc: {
        english: '',
        hindi: 'श्लेडेन + श्वान (1838-39): कोशिका सिद्धांत',
      },
    },
    {
      latex: '\\text{Virchow (1855): Omnis cellula e cellula}',
      desc: {
        english: '',
        hindi: 'विरचो (1855): ओम्निस सेल्युला ई सेल्युला',
      },
    },
    {
      latex: '\\text{Prokaryote ribosome: 70S = 50S + 30S}',
      desc: {
        english: '',
        hindi: 'प्रोकैरियोट राइबोसोम: 70S = 50S + 30S',
      },
    },
    {
      latex: '\\text{Eukaryote ribosome: 80S = 60S + 40S}',
      desc: {
        english: '',
        hindi: 'यूकेरियोट राइबोसोम: 80S = 60S + 40S',
      },
    },
    {
      latex: '\\text{Cilia/flagella axoneme: 9 + 2}',
      desc: {
        english: '',
        hindi: 'सिलिया/फ्लैगेला एक्सोनेम: 9 + 2',
      },
    },
    {
      latex: '\\text{Centriole: 9 + 0 triplets}',
      desc: {
        english: '',
        hindi: 'सेंट्रीओल: 9 + 0 त्रिक',
      },
    },
    {
      latex: '\\text{Na^+/K^+ pump: 3 Na^+ out / 2 K^+ in per ATP}',
      desc: {
        english: '',
        hindi: 'Na +/K + पंप: 3 Na + आउट / 2 K + इन प्रति एटीपी',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Organelle',
      problem: {
        english: 'Site of ribosome synthesis is:',
        hindi: 'राइबोसोम संश्लेषण का स्थल है:',
      },
      answer: {
        english: 'Nucleolus',
        hindi: 'न्यूक्लियस',
      },
      solution: {
        english: 'rRNA is synthesised and assembled with proteins in nucleolus.',
        hindi: 'आरआरएनए को न्यूक्लियोलस में प्रोटीन के साथ संश्लेषित और संयोजित किया जाता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Membrane',
      problem: {
        english: 'Fluid mosaic model was proposed by:',
        hindi: 'द्रव मोज़ेक मॉडल किसके द्वारा प्रस्तावित किया गया था:',
      },
      answer: {
        english: 'Singer and Nicolson (1972)',
        hindi: 'गायक और निकोलसन (1972)',
      },
      solution: {
        english: 'Lipid bilayer with mosaic of proteins; lateral fluidity.',
        hindi: 'प्रोटीन की मोज़ेक के साथ लिपिड बाईलेयर; पार्श्व तरलता.',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Centriole',
      problem: {
        english: 'Microtubule arrangement in centriole is:',
        hindi: 'सेंट्रीओल में सूक्ष्मनलिका व्यवस्था है:',
      },
      answer: {
        english: '9 + 0 (nine triplets, no central)',
        hindi: '9 + 0 (नौ त्रिक, कोई केंद्रीय नहीं)',
      },
      solution: {
        english: 'Distinct from cilia/flagella (9 + 2).',
        hindi: 'सिलिया/फ्लैगेला (9 + 2) से भिन्न।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Mitochondria',
      problem: {
        english: 'F₁ particles are present on:',
        hindi: 'F₁ कण मौजूद हैं:',
      },
      answer: {
        english: 'Inner mitochondrial membrane (cristae)',
        hindi: 'आंतरिक माइटोकॉन्ड्रियल झिल्ली (क्रिस्टे)',
      },
      solution: {
        english: 'F₁ contains ATP synthase head.',
        hindi: 'F₁ में एटीपी सिंथेज़ हेड होता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Lysosome',
      problem: {
        english: 'Lysosomes are also called:',
        hindi: 'लाइसोसोम को भी कहा जाता है:',
      },
      answer: {
        english: 'Suicidal bags',
        hindi: 'आत्मघाती बैग',
      },
      solution: {
        english: 'Contain hydrolytic enzymes that can digest cell on rupture.',
        hindi: 'इसमें हाइड्रोलाइटिक एंजाइम होते हैं जो टूटने पर कोशिका को पचा सकते हैं।',
      },
    },
  ],
  next: {
    slug: 'cell-cycle-division',
    title: 'Cell Cycle and Cell Division',
  },
};

export default chapter;
